/* ─────────────────────────────────────────────────────────────
   NightWorks shared page shell JS
   - Scrolling fractal terrain (earth horizon), ported from the
     original js/main.js bgCanvas.
   - Sticky scroll-nav reveal driven by IntersectionObserver.
   ───────────────────────────────────────────────────────────── */

function Terrain(opts, width, height, parentCanvas) {
  this.terCtx = parentCanvas.getContext('2d');
  this.scrollDelay = opts.scrollDelay || 90;
  this.lastScroll = Date.now();
  this.width = width;
  this.height = height;
  this.fillStyle = opts.fillStyle || '#191D4C';
  this.mHeight = opts.mHeight || height;

  this.points = [];
  let displacement = opts.displacement || 140;
  const power = Math.pow(2, Math.ceil(Math.log(width) / Math.log(2)));
  this.points[0] = this.mHeight;
  this.points[power] = this.points[0];
  for (let i = 1; i < power; i *= 2) {
    for (let j = (power / i) / 2; j < power; j += power / i) {
      this.points[j] =
        ((this.points[j - (power / i) / 2] + this.points[j + (power / i) / 2]) / 2) +
        Math.floor(Math.random() * -displacement + displacement);
    }
    displacement *= 0.6;
  }
}
Terrain.prototype.update = function () {
  this.terCtx.fillStyle = this.fillStyle;
  if (Date.now() > this.lastScroll + this.scrollDelay) {
    this.lastScroll = Date.now();
    this.points.push(this.points.shift());
  }
  this.terCtx.beginPath();
  for (let i = 0; i <= this.width; i++) {
    if (i === 0) this.terCtx.moveTo(0, this.points[0]);
    else if (this.points[i] !== undefined) this.terCtx.lineTo(i, this.points[i]);
  }
  this.terCtx.lineTo(this.width, this.height);
  this.terCtx.lineTo(0, this.height);
  this.terCtx.lineTo(0, this.points[0]);
  this.terCtx.fill();
};

function initTerrains() {
  document.querySelectorAll('canvas.terrain-canvas').forEach(canvas => {
    if (!canvas.offsetParent) return;
    if (canvas.dataset.inited) return;
    canvas.dataset.inited = '1';
    const wrap = canvas.parentElement;
    const r = wrap.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = r.width * dpr;
    canvas.height = r.height * dpr;
    canvas.style.width = r.width + 'px';
    canvas.style.height = r.height + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const W = r.width, H = r.height;
    const entities = [
      new Terrain({ displacement: 100, scrollDelay: 90, fillStyle: 'rgba(36, 56, 78, 0.95)', mHeight: H * 0.55 }, W, H, canvas),
      new Terrain({ displacement: 120, scrollDelay: 50, fillStyle: 'rgb(17, 22, 40)',          mHeight: H * 0.70 }, W, H, canvas),
      new Terrain({ displacement: 100, scrollDelay: 20, fillStyle: 'rgb(0, 0, 0)',             mHeight: H * 0.85 }, W, H, canvas),
    ];
    (function animate() {
      ctx.clearRect(0, 0, W, H);
      entities.forEach(e => e.update());
      requestAnimationFrame(animate);
    })();
  });
}

/* Sticky scroll-nav. If a [data-hero-logo] element exists, reveal the
   nav once it scrolls out of view. Otherwise (sub-page with no big
   hero logo) the nav stays solid via the .always-solid class. */
function initScrollNav() {
  const nav = document.querySelector('.scroll-nav');
  if (!nav || nav.dataset.inited) return;
  const target = document.querySelector('[data-hero-logo]');
  if (!target) { nav.classList.add('always-solid'); nav.dataset.inited = '1'; return; }
  nav.dataset.inited = '1';
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting && e.intersectionRatio > 0.15) nav.classList.remove('scrolled');
      else nav.classList.add('scrolled');
    });
  }, { threshold: [0, 0.15, 0.5, 1] });
  io.observe(target);
}

/* Slow the hero video down — gentler motion and the loop seam is hit
   far less often, softening the visible reset at the end of the clip. */
function initHeroVideo() {
  document.querySelectorAll('video.hero-video').forEach(v => {
    const setRate = () => { v.playbackRate = 0.5; };
    setRate();
    v.addEventListener('loadedmetadata', setRate);
    v.addEventListener('play', setRate);
  });
}

/* Contact form — AJAX submit to the Cloudflare Pages Function. */
function initContactForm() {
  document.querySelectorAll('form.contact-form').forEach(form => {
    const setStatus = (msg, isError) => {
      let el = form.querySelector('.form-status');
      if (!el) { el = document.createElement('p'); el.className = 'form-status'; form.appendChild(el); }
      el.textContent = msg;
      el.classList.toggle('error', !!isError);
    };
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('button[type="submit"]');
      const data = Object.fromEntries(new FormData(form).entries());
      // Turnstile injects a hidden "cf-turnstile-response" field once solved.
      if (form.querySelector('.cf-turnstile') && !data['cf-turnstile-response']) {
        setStatus('Please complete the verification first.', true);
        return;
      }
      if (btn) btn.disabled = true;
      setStatus('Sending…', false);
      try {
        const res = await fetch(form.getAttribute('action') || '/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        const out = await res.json().catch(() => ({}));
        if (!res.ok || !out.ok) throw new Error(out.error || 'Failed');
        form.reset();
        setStatus("Thanks — your message is on its way. I'll be in touch.", false);
      } catch (err) {
        setStatus((err && err.message) ? err.message + ' — or email info@nightworks.io.' : 'Something went wrong — email info@nightworks.io.', true);
      } finally {
        if (btn) btn.disabled = false;
        // Turnstile tokens are single-use — refresh for the next attempt.
        if (window.turnstile) window.turnstile.reset();
      }
    });
  });
}

/* Mobile nav — hamburger toggles the dropdown; links close it. */
function initNav() {
  document.querySelectorAll('.scroll-nav').forEach(nav => {
    const btn = nav.querySelector('.nav-toggle');
    if (btn) btn.addEventListener('click', () => nav.classList.toggle('open'));
    nav.querySelectorAll('ul a').forEach(a => a.addEventListener('click', () => nav.classList.remove('open')));
  });
}

window.addEventListener('load', function () { initTerrains(); initScrollNav(); initHeroVideo(); initContactForm(); initNav(); });
